import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, RadioGroup, Switch } from "@progress/kendo-react-inputs";
import { Hint } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { compareDesc } from "date-fns/esm";
import { debounce } from "lodash";
import { setWorkflowCustomVariables } from "../../../../actions/taskSidebar";
import { button, icon, label, notifyIcon, number, quote } from "../../../../config";
import { useTeamChipPersistence } from "../../../../shared/components/ChipEditor/TeamChipPersistenceContext";
import { getNotification } from "../../../../utils/common";
import ShowIfSection from "../../../Forms/ShowIfSection";
import { useFormInteraction } from "../../../Forms/contexts/FormInteractionContext";
import { deleteWorkflowOrTeamChipVariable } from "../../../TeamSetting/TeamChips/projectVariables.helper";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { useVariablesContext } from "../context/variableContext";
import { usePayloadBuilder } from "../hooks/usePayloadBuilder";
import { useAdminVariableHelper, useFormChipHelper, useLockTeamChipPresetFields, useUniqueVariableNames } from "../hooks/workflowVariables.hooks";
import "../workflowVariable.scss";
import { CHIP_TYPE_DATA, WORKFLOW_VARIABLE_LABELS, getDateFormatOptions } from "../workflowVariables.constants";
import ExpressionEditor from "./ExpressionEditor";
import VariableNameHeader from "./common/VariableNameHeader";

/**
 * used to render workflow variable configuration for date type variables
 * @author {Prachi Jain}
 */
const DateConfiguration = ({
  isFormVariable,
  usedWorkflowVariableIds = [],
  onRemoveFromForm,
  formData,
  formId,
  onPanelClose,
  showClickToRemoveFromFormBanner = true,
  handleShowIfChange = () => {},
}) => {
  const [isNameEmpty, setIsNameEmpty] = useState(false);
  const [isEditName, setIsEditName] = useState(false);
  const [showExpressionEditor, setShowExpressionEditor] = useState(false);
  const { selectedElement } = useFormInteraction();
  const {
    variableName,
    setVariableName,
    isRequired,
    setIsRequired,
    chipType,
    setChipType,
    isDuplicateName,
    isPlaceholder,
    setIsPlaceholder,
    placeholderText,
    setPlaceholderText,
    isDefaultAnswer,
    setIsDefaultAnswer,
    defaultValue,
    setDefaultValue,
    dateFormat,
    setDateFormat,
    dateSeparator,
    setDateSeparator,
    pastDates,
    setPastDates,
    futureDates,
    setFutureDates,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    isStartDate,
    setIsStartDate,
    isEndDate,
    setIsEndDate,
    dateFormatOptions,
    isAutomaticallySetValue,
    setIsAutomaticallySetValue,
    setExpressionValue,
    setDateFormatOptions,
    hasVarChanges,
    setHasVarChanges,
    showChipName,
    setShowChipName,
  } = useVariablesContext();
  const { buildDatePayload } = usePayloadBuilder();
  const {
    customWorkflowVariables,
    setCustomWorkflowVariables,
    isNewWorkflowVariable,
    setIsNewWorkflowVariable,
    selectedVariable,
    setSelectedVariable,
    workflow,
    setToggleVariableList,
  } = useWorkflowContext();

  const teamChipPersist = useTeamChipPersistence();
  const { saveVariableForExistingWorkflow, saveVariableForNewWorkflow } = useAdminVariableHelper();
  const { checkIsDuplicateName } = useUniqueVariableNames();
  const { updateFormChipProperty, isFormSpecificVisible } = useFormChipHelper();
  const [changeCounter, setChangeCounter] = useState(0);
  const dispatch = useDispatch();

  // Check if the chip is used in the form (for Form Specific properties)
  const isChipUsedInForm = useMemo(() => {
    if (!isFormVariable) return true; // Not relevant for workflow designer
    return usedWorkflowVariableIds.includes(selectedVariable?.id);
  }, [isFormVariable, usedWorkflowVariableIds, selectedVariable?.id]);

  const lockTeamChipPresetFields = useLockTeamChipPresetFields(isFormVariable, selectedVariable);

  const dateSeparatorOptions = [
    { label: "/", value: 1 },
    { label: "-", value: 2 },
    { label: ".", value: 3 },
  ];

  const defaultValueOptions = ["Current Date", "Select Date"];

  useEffect(() => {
    if (isNewWorkflowVariable) {
      setDateSeparator(dateSeparatorOptions[0]);
      const defaultDateFormatOptions = getDateFormatOptions("/");
      setDateFormatOptions(defaultDateFormatOptions);
      setDateFormat(defaultDateFormatOptions[0]);
      setDefaultValue(defaultValueOptions[0]);
      handleVariableChange();
    }
  }, []);

  const saveDateVariable = async () => {
    if (isNameEmpty) {
      getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_REQUIRED_ERROR, notifyIcon.WARNING_ICON);
      return;
    }
    if (isDuplicateName) {
      getNotification(WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_DUPLICATE_ERROR, notifyIcon.WARNING_ICON);
      return;
    }
    if (startDate && endDate && compareDesc(new Date(startDate), new Date(endDate)) === -1)
      return getNotification(WORKFLOW_VARIABLE_LABELS.START_DATE_GREATER_THAN_END_DATE, notifyIcon.ERROR_ICON);
    let payload = buildDatePayload();
    let savedVariable = null;

    if (workflow?.Id) {
      const currentId = payload?.id || selectedVariable?.id;
      if (typeof currentId === "number" && !isNewWorkflowVariable) {
        delete payload.id;
      }
      savedVariable = await saveVariableForExistingWorkflow(payload);
      if (savedVariable && setSelectedVariable) {
        setSelectedVariable(savedVariable);
      }
    } else {
      savedVariable = await saveVariableForNewWorkflow(payload);
      if (teamChipPersist?.projectId && !savedVariable) {
        return null;
      }
      if (!savedVariable) {
        savedVariable = { ...selectedVariable, attributes: { ...selectedVariable?.attributes, ...payload.attributes } };
      }
    }
    setIsNewWorkflowVariable(false);
    setHasVarChanges(false);
    return savedVariable;
  };

  /**
   * Debounced auto-save function using lodash debounce
   * Automatically saves date variable configuration after 2 seconds of inactivity
   * @author Abhishek Dhillon
   */
  const debouncedAutoSave = useCallback(
    debounce(async () => {
      try {
        await saveDateVariable();
      } catch (error) {
        console.error("Auto-save failed:", error);
      }
    }, 2000),
    [saveDateVariable]
  );

  /**
   * Common function to handle variable changes and trigger auto-save
   * Replaces all handleVariableChange() calls
   * @author Abhishek Dhillon
   */
  const handleVariableChange = useCallback(() => {
    setHasVarChanges(true);
    setChangeCounter((prev) => prev + 1);
  }, [setHasVarChanges]);

  /**
   * Auto-save effect with debouncing for form variables only
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    if (isFormVariable && changeCounter > 0) {
      debouncedAutoSave();
    }
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [changeCounter, isFormVariable]);

  /** Team Chips uses projectVariables delete when no workflow Id */
  const removeVariableHandler = async () => {
    const removedId = selectedVariable?.id;
    const deleteSucceeded = await deleteWorkflowOrTeamChipVariable({
      selectedVariable,
      workflow,
      isNewWorkflowVariable,
      teamChipPersist,
    });
    if (!deleteSucceeded) return;
    setToggleVariableList(false);
    setIsNewWorkflowVariable(false);
    setSelectedVariable(null);
    const filteredVariableList = customWorkflowVariables?.filter((variable) => variable.id !== removedId);
    setCustomWorkflowVariables(filteredVariableList);
    dispatch(setWorkflowCustomVariables(filteredVariableList));
  };

  /**
   * Handles variable name change with validation and change tracking
   * @author Abhishek Dhillon
   * @param {Event} event - Input change event
   */
  const handleVariableNameChange = (event) => {
    const newVariableName = event.target.value;
    setVariableName(newVariableName);
    checkIsDuplicateName(newVariableName?.trim());
    if (!newVariableName?.trim()) {
      setIsNameEmpty(true);
      setHasVarChanges(false);
      return;
    }
    setIsNameEmpty(false);
    handleVariableChange();
  };

  /**
   * Handles date separator change and tracks changes
   * @author Abhishek Dhillon
   * @param {Event} e - Dropdown change event
   */
  const handleSeparatorChange = (e) => {
    setDateSeparator(e.value);
    const updatedDateFormatOptions = getDateFormatOptions(e.value.label);
    setDateFormatOptions(updatedDateFormatOptions);
    setDateFormat(updatedDateFormatOptions[0]);
    handleVariableChange();
  };
  return (
    <div className='position-relative'>
      <div className='d-flex flex-column border-bottom overflow-y-auto overflow-x-hidden date-configuration-section dropdown-configuration-section'>
        <div className='justify-content-between'>
          <VariableNameHeader
            isNameEmpty={isNameEmpty}
            setIsNameEmpty={setIsNameEmpty}
            selectedVariable={selectedVariable}
            removeVariableHandler={removeVariableHandler}
            handleVariableChange={handleVariableChange}
            formData={formData}
            formId={formId}
            onPanelClose={onPanelClose}
            isDeleteDisabled={teamChipPersist?.allowDelete === false}
            isFormVariable={isFormVariable}
            onVariableNameCommitted={!isFormVariable ? saveDateVariable : undefined}
          />
        </div>
        {showClickToRemoveFromFormBanner && isChipUsedInForm && onRemoveFromForm && (
          <div className='form-group mb-2'>
            <div
              className='information-panel w-100 d-flex panel-info cursor-pointer'
              onClick={() => {
                if (onRemoveFromForm && selectedVariable?.id) {
                  onRemoveFromForm(selectedVariable.id);
                }
              }}>
              <span className='panel-icon d-flex align-items-center'>{icon.INFO_ICON}</span>
              <div className='panel-body'>
                <div className='panel-message'>{quote.CLICK_TO_REMOVE_FROM_FORM}</div>
              </div>
            </div>
          </div>
        )}

        {/* Info banner when chip is not added to form */}
        {!isChipUsedInForm && (
          <div className='form-group mb-2 cursor-default'>
            <div className='information-panel w-100 d-flex panel-info'>
              <span className='panel-icon d-flex align-items-center'>{icon.INFO_ICON}</span>
              <div className='panel-body'>
                <div className='panel-message'>{quote.UNUSED_CHIP_INFO}</div>
              </div>
            </div>
          </div>
        )}
        <div className='custom-variable-properties pr-2'>
          {/* Automatically Set Value Toggle - Only show in form builder when formId exists */}
          {isFormVariable && isFormSpecificVisible && (
            <>
              <div className='form-section-label mb-2 mt-1 bold '>Form Specific</div>
              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='automatically-set-value-conf' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.AUTOMATICALLY_SET_VALUE}{" "}
                    {isAutomaticallySetValue && (
                      <span
                        className='cursor-pointer'
                        style={{ color: "#1976d2", textDecoration: "underline" }}
                        onClick={(e) => {
                          e.preventDefault();
                          setShowExpressionEditor(true);
                        }}>
                        ({WORKFLOW_VARIABLE_LABELS.EDIT_EXPRESSION})
                      </span>
                    )}
                  </label>
                  <Switch
                    id='automatically-set-value-conf'
                    className='mb-1 toggle-switch-margin'
                    checked={isAutomaticallySetValue}
                    disabled={!isChipUsedInForm}
                    onChange={() => {
                      const newValue = !isAutomaticallySetValue;
                      setIsAutomaticallySetValue(newValue);
                      updateFormChipProperty("autoSetValue", newValue);
                    }}
                  />
                </div>
              </div>

              {/* Show If - Placeholder for future implementation */}
              <ShowIfSection // Show If Section
                selectedElement={selectedElement}
                handleShowIfChange={handleShowIfChange}
              />

              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='date-format' className='mr-3 font-weight-normal' style={{ width: "80%" }}>
                    {WORKFLOW_VARIABLE_LABELS.DATE_FORMAT}
                  </label>
                  <DropDownList
                    id='date-format'
                    className='mb-1'
                    value={dateFormat}
                    disabled={!isChipUsedInForm || isAutomaticallySetValue}
                    onChange={(e) => {
                      setDateFormat(e.value);
                      updateFormChipProperty("dateFormat", e.value);
                    }}
                    data={dateFormatOptions}
                    textField='label'
                    valueField='value'
                  />
                </div>
              </div>
              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='date-separator' className='mr-3 font-weight-normal' style={{ width: "80%" }}>
                    {WORKFLOW_VARIABLE_LABELS.DATE_SEPARATOR}
                  </label>
                  <DropDownList
                    id='date-separator'
                    className='mb-1'
                    value={dateSeparator}
                    disabled={!isChipUsedInForm || isAutomaticallySetValue}
                    onChange={(e) => {
                      setDateSeparator(e.value);
                      const updatedDateFormatOptions = getDateFormatOptions(e.value.label);
                      setDateFormatOptions(updatedDateFormatOptions);
                      setDateFormat(updatedDateFormatOptions[0]);
                      updateFormChipProperty("dateSeparator", e.value);
                    }}
                    data={dateSeparatorOptions}
                    textField='label'
                    valueField='value'
                  />
                </div>
              </div>
              {/* Chip Name Configuration */}
              <div className='form-group mb-1'>
                <div className='d-flex justify-content-between mb-2'>
                  <label
                    htmlFor='chip-name-conf'
                    className='mr-2 mb-0 d-flex align-items-center font-weight-normal'
                    style={{ whiteSpace: "nowrap", height: "32px" }}>
                    Chip Name
                  </label>
                  <div className='d-flex align-items-center gap-2'>
                    {/* Toggle Style Buttons */}
                    <div className='d-flex' style={{ borderRadius: "6px", overflow: "hidden", border: "1px solid #d4d4d8" }}>
                      <button
                        type='button'
                        className={`btn ${showChipName ? "btn-primary" : "btn-outline-secondary"}`}
                        disabled={!isChipUsedInForm || isAutomaticallySetValue}
                        onClick={() => {
                          setShowChipName(true);
                          updateFormChipProperty("showChipName", true);
                        }}
                        style={{
                          borderRadius: 0,
                          border: "none",
                          fontSize: "12px",
                          padding: "6px 12px",
                          backgroundColor: showChipName ? "#007bff" : "#ffffff",
                          color: showChipName ? "#ffffff" : "#6c757d",
                          borderRight: "1px solid #d4d4d8",
                        }}>
                        Show
                      </button>
                      <button
                        type='button'
                        className={`btn ${!showChipName ? "btn-primary" : "btn-outline-secondary"}`}
                        disabled={!isChipUsedInForm || isAutomaticallySetValue}
                        onClick={() => {
                          setShowChipName(false);
                          updateFormChipProperty("showChipName", false);
                        }}
                        style={{
                          borderRadius: 0,
                          border: "none",
                          fontSize: "12px",
                          padding: "6px 12px",
                          backgroundColor: !showChipName ? "#007bff" : "#ffffff",
                          color: !showChipName ? "#ffffff" : "#6c757d",
                        }}>
                        Hide
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className='form-group mb-1'>
                <div className='d-flex align-items-center justify-content-between'>
                  <label htmlFor='date-default-answer' className='mr-3 font-weight-normal'>
                    {WORKFLOW_VARIABLE_LABELS.DEFAULT_SELECTION}
                  </label>
                  <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
                    <div>
                      <Switch
                        id='date-default-answer'
                        className='mb-1 toggle-switch-margin'
                        checked={isDefaultAnswer}
                        disabled={isAutomaticallySetValue}
                        onChange={(e) => {
                          setIsDefaultAnswer(!isDefaultAnswer);
                          setDefaultValue(defaultValueOptions[0]);
                          updateFormChipProperty("defaultValue", e.target.value ? defaultValueOptions[0] : null);
                        }}
                      />
                    </div>
                  </Tooltip>
                </div>
                {isDefaultAnswer && (
                  <DropDownList
                    id='date-default-answer-value'
                    className='mb-1'
                    value={defaultValue}
                    disabled={isAutomaticallySetValue}
                    data={defaultValueOptions}
                    onChange={(e) => {
                      setDefaultValue(e.target.value);
                      updateFormChipProperty("defaultValue", e.target.value || null);
                    }}
                  />
                )}
              </div>

              {
                <div className='form-group mb-1'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <label htmlFor='date-required-field' className='mr-3 font-weight-normal'>
                      {WORKFLOW_VARIABLE_LABELS.REQUIRED}
                    </label>
                    <Switch
                      id='date-required-field'
                      className='mb-1 toggle-switch-margin'
                      checked={isRequired}
                      disabled={isAutomaticallySetValue}
                      onChange={() => {
                        setIsRequired(!isRequired);
                        updateFormChipProperty("required", !isRequired);
                      }}
                    />
                  </div>
                </div>
              }
            </>
          )}
          <div className='form-section-label mb-2 bold '>General </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='date-placeholder' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.PLACEHOLDER}
              </label>
              <Switch
                id='date-placeholder'
                className='mb-1 toggle-switch-margin'
                checked={isPlaceholder}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsPlaceholder(!isPlaceholder);
                  handleVariableChange();
                }}
              />
            </div>
            {isPlaceholder && (
              <Input
                id='workflow-variable-date-placeholder'
                placeholder='Enter Placeholder'
                value={placeholderText}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={(e) => {
                  setPlaceholderText(e.target.value);
                  handleVariableChange();
                }}
              />
            )}
          </div>

          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='date-past-dates' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.PAST_DATES}
              </label>
              <Switch
                id='date-past-dates'
                className='mb-1 toggle-switch-margin'
                checked={pastDates}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setPastDates(!pastDates);
                  handleVariableChange();
                }}
              />
            </div>
          </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='date-future-dates' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.FUTURE_DATES}
              </label>
              <Switch
                id='date-future-dates'
                className='mb-1 toggle-switch-margin'
                checked={futureDates}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setFutureDates(!futureDates);
                  handleVariableChange();
                }}
              />
            </div>
          </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='date-start-date' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.START_DATE}
              </label>
              <Switch
                id='date-start-date'
                className='mb-1 toggle-switch-margin'
                checked={isStartDate}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsStartDate(!isStartDate);
                  handleVariableChange();
                }}
              />
            </div>
            {isStartDate && (
              <DatePicker
                id='date-start-date-value'
                className='mb-1'
                value={startDate ? new Date(startDate) : null}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  handleVariableChange();
                }}
                format={dateFormat?.label}
                placeholder={dateFormat?.label}
              />
            )}
          </div>
          <div className='form-group mb-1'>
            <div className='d-flex align-items-center justify-content-between'>
              <label htmlFor='date-end-date' className='mr-3 font-weight-normal'>
                {WORKFLOW_VARIABLE_LABELS.END_DATE}
              </label>
              <Switch
                id='date-end-date'
                className='mb-1 toggle-switch-margin'
                checked={isEndDate}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={() => {
                  setIsEndDate(!isEndDate);
                  handleVariableChange();
                }}
              />
            </div>
            {isEndDate && (
              <DatePicker
                id='date-end-date-value'
                className='mb-1'
                value={endDate ? new Date(endDate) : null}
                disabled={lockTeamChipPresetFields || isAutomaticallySetValue}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  handleVariableChange();
                }}
                {...(startDate && { min: new Date(startDate) })}
                format={dateFormat?.label}
                placeholder={dateFormat?.label}
              />
            )}
          </div>
        </div>
      </div>
      {showExpressionEditor && (
        <ExpressionEditor
          isOpen={showExpressionEditor}
          onClose={() => {
            setShowExpressionEditor(false);
          }}
          onSave={(expression) => {
            setExpressionValue(expression);
            setShowExpressionEditor(false);
          }}
          currentVariableName={variableName}
          expectedVariableType='date'
          selectedVariable={selectedVariable}
          workflow={workflow}
          isNewWorkflowVariable={isNewWorkflowVariable}
          onSaveVariable={saveDateVariable}
        />
      )}
      {!isFormVariable && (
        <div className='update-button-container w-100 pt-3 text-right'>
          <button
            className='btn btn-primary'
            disabled={
              isDuplicateName ||
              isNameEmpty ||
              (startDate && endDate && compareDesc(new Date(startDate), new Date(endDate)) === -1) ||
              lockTeamChipPresetFields
            }
            onClick={saveDateVariable}>
            {isNewWorkflowVariable ? button.SAVE : label.UPDATE}
          </button>
        </div>
      )}
    </div>
  );
};

export default DateConfiguration;
